import { useEffect, useState } from 'react';

import { buildAddressLabel } from '@forms/autoAddress/AutoAddressHelper';
import { FormFieldError } from '@forms/FormFieldError/FormFieldError';
import {
    GetPostcodeLookupResponse,
    fetchGetPostcodeLookup
} from '@iwoca/lapi-client/edge';
import {
    Button,
    ComboBox,
    ComboBoxElement,
    ComboBoxOption,
    Input,
    Spinner
} from '@iwoca/orion';
import { useQuery } from '@tanstack/react-query';
import {
    FieldErrorsImpl,
    UseFormGetValues,
    UseFormRegister,
    UseFormSetValue
} from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';

import { TFormValues } from '../MSHForm.types';
import styles from '../MSHModal.module.css';

const AddressFields = ({
    errors,
    register,
    getValues,
    setValue,
    disabled = false
}: {
    errors: FieldErrorsImpl<TFormValues>;
    register: UseFormRegister<TFormValues>;
    getValues: UseFormGetValues<TFormValues>;
    setValue: UseFormSetValue<TFormValues>;
    disabled?: boolean;
}) => {
    type TPostcodeAddress = GetPostcodeLookupResponse['data'][number];

    const intl = useIntl();
    const [postcodeSearchTerm, setPostcodeSearchTerm] = useState('');
    const [debouncedPostcodeSearchTerm, setDebouncedPostcodeSearchTerm] =
        useState('');
    const [isManuallyEnteringAddress, setIsManuallyEnteringAddress] =
        useState(false);

    useEffect(() => {
        const timeout = setTimeout(() => {
            setDebouncedPostcodeSearchTerm(postcodeSearchTerm);
        }, 500);

        return () => clearTimeout(timeout);
    }, [postcodeSearchTerm]);

    // Get addresses
    const { data: addresses, isLoading: addressesLoading } = useQuery({
        queryKey: ['postcode_lookup', debouncedPostcodeSearchTerm],
        queryFn: async () => {
            try {
                const res = await fetchGetPostcodeLookup({
                    query: { postcode: debouncedPostcodeSearchTerm }
                });
                return res;
            } catch (e) {
                console.error(e);
                return { data: [] };
            }
        },

        enabled: debouncedPostcodeSearchTerm !== '',
        select: (res) => res!.data
    });

    const onChange = (value: TPostcodeAddress) => {
        if (!value) return;

        if (value.house_name) {
            setValue('address.house_name', value.house_name, {
                shouldValidate: true
            });
        }

        if (value.house_number) {
            setValue('address.house_number', value.house_number, {
                shouldValidate: true
            });
        }

        if (value.street_line_1) {
            setValue('address.street_line_1', value.street_line_1, {
                shouldValidate: true
            });
        }

        if (value.street_line_2) {
            setValue('address.street_line_2', value.street_line_2, {
                shouldValidate: true
            });
        }

        if (value.town) {
            setValue('address.town', value.town, {
                shouldValidate: true
            });
        }

        if (value.postcode) {
            setValue('address.postcode', value.postcode, {
                shouldValidate: true
            });
        }

        if (value.ptc_abs_code) {
            setValue('address.equifax_token', value.ptc_abs_code);
        }
    };

    return (
        <div className={`${styles.fields} ${styles.addressFields}`}>
            <h3 className={styles.addressFieldsTitle}>
                <FormattedMessage
                    id="_.modal.major_shareholder_check_form.address_fields_title"
                    defaultMessage="Address details:"
                />
            </h3>

            <div className={styles.addressList}>
                <div className={styles.address}>
                    {isManuallyEnteringAddress ? (
                        <div className={styles.addressManualFields}>
                            <div className={styles.houseNumber}>
                                <label className={styles.label}>
                                    <FormattedMessage
                                        id="_.modal.major_shareholder_check_form.house_number_field"
                                        defaultMessage="House number"
                                    />
                                </label>

                                {/* eslint-disable-next-line iwoca/input-a11y */}
                                <Input
                                    {...register('address.house_number')}
                                    placeholder={intl.formatMessage({
                                        id: '_.modal.major_shareholder_check_form.house_number_field_placeholder',
                                        defaultMessage: 'House number'
                                    })}
                                    disabled={disabled}
                                />
                            </div>

                            <div className={styles.houseName}>
                                <label className={styles.label}>
                                    <FormattedMessage
                                        id="_.modal.major_shareholder_check_form.house_name_field"
                                        defaultMessage="House name"
                                    />
                                </label>
                                {/* eslint-disable-next-line iwoca/input-a11y */}
                                <Input
                                    {...register('address.house_name')}
                                    placeholder={intl.formatMessage({
                                        id: '_.modal.major_shareholder_check_form.house_name_field_placeholder',
                                        defaultMessage: 'House name'
                                    })}
                                    disabled={disabled}
                                />
                            </div>

                            <div className={styles.addressManualField}>
                                <label className={styles.label}>
                                    <FormattedMessage
                                        id="_.modal.major_shareholder_check_form.street_line_one_field"
                                        defaultMessage="Street line 1"
                                    />
                                </label>
                                {/* eslint-disable-next-line iwoca/input-a11y */}
                                <Input
                                    {...register('address.street_line_1')}
                                    placeholder={intl.formatMessage({
                                        id: '_.modal.major_shareholder_check_form.street_line_one_field_placeholder',
                                        defaultMessage: 'Street line 1'
                                    })}
                                    disabled={disabled}
                                />
                            </div>
                            <div className={styles.addressManualField}>
                                <label className={styles.label}>
                                    <FormattedMessage
                                        id="_.modal.major_shareholder_check_form.street_line_two_field"
                                        defaultMessage="Street line 2"
                                    />
                                </label>
                                {/* eslint-disable-next-line iwoca/input-a11y */}
                                <Input
                                    {...register('address.street_line_2')}
                                    placeholder={intl.formatMessage({
                                        id: '_.modal.major_shareholder_check_form.street_line_two_field_placeholder',
                                        defaultMessage: 'Street line 2'
                                    })}
                                    disabled={disabled}
                                />
                            </div>

                            <div className={`${styles.field} ${styles.town}`}>
                                {errors.address?.town && (
                                    <FormFieldError
                                        message={intl.formatMessage({
                                            id: '_.modal.major_shareholder_check_form.error.valid_town_required',
                                            defaultMessage: 'Town required'
                                        })}
                                    />
                                )}
                                <label className={styles.label}>
                                    <FormattedMessage
                                        id="_.modal.major_shareholder_check_form.town_field"
                                        defaultMessage="Town"
                                    />
                                </label>
                                {/* eslint-disable-next-line iwoca/input-a11y */}
                                <Input
                                    {...register('address.town', {
                                        required: true
                                    })}
                                    placeholder={intl.formatMessage({
                                        id: '_.modal.major_shareholder_check_form.town_field_placeholder',
                                        defaultMessage: 'Town'
                                    })}
                                    disabled={disabled}
                                />
                            </div>
                            <div className={styles.postcode}>
                                {errors.address?.postcode && (
                                    <FormFieldError
                                        message={intl.formatMessage({
                                            id: '_.modal.major_shareholder_check_form.error.valid_postcode_required',
                                            defaultMessage: 'Postcode required'
                                        })}
                                    />
                                )}
                                <label
                                    className={styles.label}
                                    htmlFor="postcode"
                                >
                                    <FormattedMessage
                                        id="_.modal.major_shareholder_check_form.postcode_manual_field"
                                        defaultMessage="Postcode"
                                    />
                                </label>
                                {/* eslint-disable-next-line iwoca/input-a11y */}
                                <Input
                                    {...register('address.postcode', {
                                        required: true
                                    })}
                                    placeholder={intl.formatMessage({
                                        id: '_.modal.major_shareholder_check_form.postcode_manual_field_placeholder',
                                        defaultMessage: 'Postcode'
                                    })}
                                    id="postcode"
                                    disabled={disabled}
                                />
                            </div>
                        </div>
                    ) : (
                        <div className={`${styles.addressSearchWrap} `}>
                            {errors.address?.postcode && (
                                <FormFieldError
                                    className={styles.addressSearchError}
                                    message={intl.formatMessage({
                                        id: '_.modal.major_shareholder_check_form.error.valid_address_required',
                                        defaultMessage:
                                            'Please enter an address'
                                    })}
                                />
                            )}

                            <ComboBox
                                {...register('address.postcode', {
                                    required: true
                                })}
                                className={styles.addressSearchCombobox}
                                onQueryChange={(val) => {
                                    setPostcodeSearchTerm(val);
                                }}
                                onOptionSelect={(value) => {
                                    onChange(JSON.parse(value));
                                }}
                                value={buildAddressLabel(getValues('address'))}
                                placeholder={intl.formatMessage({
                                    id: '_.modal.major_shareholder_check_form.postcode_field_placeholder',
                                    defaultMessage: 'Enter a postcode'
                                })}
                                disabled={disabled}
                            >
                                {!postcodeSearchTerm && (
                                    <ComboBoxElement>
                                        <FormattedMessage
                                            id="_.modal.major_shareholder_check_form.empty_postcode_error"
                                            defaultMessage="Enter a postcode"
                                        />
                                    </ComboBoxElement>
                                )}

                                {postcodeSearchTerm && addressesLoading && (
                                    <ComboBoxElement>
                                        <Spinner />
                                    </ComboBoxElement>
                                )}

                                {postcodeSearchTerm &&
                                    addresses?.length === 0 && (
                                        <ComboBoxElement>
                                            <FormattedMessage
                                                id="_.modal.major_shareholder_check_form.no_matching_postcodes_error"
                                                defaultMessage="There are no matching postcodes"
                                            />
                                        </ComboBoxElement>
                                    )}

                                {postcodeSearchTerm &&
                                    addresses &&
                                    addresses.map((address, index) => (
                                        <ComboBoxOption
                                            key={`${address.postcode}-${index}`}
                                            value={JSON.stringify(address)}
                                        >
                                            {buildAddressLabel(address)}
                                        </ComboBoxOption>
                                    ))}
                            </ComboBox>
                        </div>
                    )}
                    <Button
                        variant="tertiary"
                        onClick={() =>
                            setIsManuallyEnteringAddress((prev) => !prev)
                        }
                        type="button"
                        disabled={disabled}
                    >
                        {isManuallyEnteringAddress ? (
                            <FormattedMessage
                                id="_.modal.major_shareholder_check_form.toggle_enter_postcode_cta"
                                defaultMessage="Search for my address"
                            />
                        ) : (
                            <FormattedMessage
                                id="_.modal.major_shareholder_check_form.toggle_manual_address_cta"
                                defaultMessage="Enter address manually"
                            />
                        )}
                    </Button>
                </div>
            </div>
        </div>
    );
};

// eslint-disable-next-line import/no-default-export
export default AddressFields;
